import useTakeSnapshot from 'pages/Editor/sections/Flow/hooks/useTakeSnapshot';
import { useMemo, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useReactFlow } from 'reactflow';

import { BlockData, CSVFormValues } from 'types';
import { code2LargeAlphabetString } from 'utils/commonUtils';

interface UseCSVDataFormParams {
  id: string;
  blockData: BlockData;
}

const useCSVDataForm = ({ id, blockData }: UseCSVDataFormParams) => {
  const [data, setData] = useState(blockData);
  const defaultValues = useMemo(() => createDefaultValues(data), [data]);

  const [columnLabels, setColumnsLabels] = useState(createTableColumnLabels(defaultValues.columnNames.length));
  const useFormReturn = useForm<CSVFormValues>({
    defaultValues,
  });

  const { takeSnapshot } = useTakeSnapshot();

  const { setNodes } = useReactFlow();
  const { setValue, getValues } = useFormReturn;

  const nodeDataSave = () => {
    const values = { ...getValues() };

    const parameters = {
      seperator: values.seperator !== 'custom' ? values.seperator : values.seperatorValue,
      column_names: values.columnNameType === 'index' ? [] : [...values.columnNames],
      delete_columns: [...values.deleteColumns],
      target_columns: [...values.targetColumns],
      id_column: values.idColumn !== 'none' ? Number(values.idColumn) : null,
      column_types: [...values.columnTypes],
      nan: values.nan !== 'none' ? values.nan : null,
      fill_nan_value:
        values.nan === 'fill'
          ? [...values.fillNanValues].map((value) => {
              if (value === '') return null;
              return value;
            })
          : [],
      encoding: values.encoding !== 'none' ? values.encoding : null,
      encoding_columns: values.encoding === 'label' ? [...values.encodingColumns] : [],
      scaler: values.scaler !== 'none' ? values.scaler : null,
      scaler_columns: values.scaler !== 'none' ? [...values.scalerColumns] : [],
    } as Record<string, any>;

    const newDataParameters = data.parameters.map((parameter) => ({
      ...parameter,
      value: parameters[parameter.param],
    }));

    setNodes((prevNodes) =>
      prevNodes.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              name: values.name,
              parameters: newDataParameters,
            },
          };
        }
        return node;
      })
    );
    setData((prevData) => ({ ...prevData, parameters: newDataParameters }));
    takeSnapshot({ undoRedo: false });
  };

  const addColumn = () => {
    const newTotalColumns = getValues('columnNames').length + 1;

    if (newTotalColumns >= columnLabels.length) {
      setColumnsLabels((prevLabels) => {
        const newLabel = code2LargeAlphabetString(prevLabels.length);
        return [...prevLabels, newLabel];
      });
    }

    setValue('columnTypes', [...getValues('columnTypes'), 'auto']);
    setValue('deleteColumns', [...getValues('deleteColumns'), false]);
    setValue('targetColumns', [...getValues('targetColumns'), false]);
    setValue('fillNanValues', [...getValues('fillNanValues'), '']);
    setValue('encodingColumns', [...getValues('encodingColumns'), false]);
    setValue('scalerColumns', [...getValues('scalerColumns'), false]);
    setValue('columnNames', [...getValues('columnNames'), '']);

    nodeDataSave();
  };

  const deleteColumns = (indexs: number[]) => {
    setValue(
      'columnTypes',
      getValues('columnTypes').filter((_, i) => !indexs.includes(i))
    );
    setValue(
      'deleteColumns',
      getValues('deleteColumns').filter((_, i) => !indexs.includes(i))
    );
    setValue(
      'targetColumns',
      getValues('targetColumns').filter((_, i) => !indexs.includes(i))
    );
    setValue(
      'fillNanValues',
      getValues('fillNanValues').filter((_, i) => !indexs.includes(i))
    );
    setValue(
      'encodingColumns',
      getValues('encodingColumns').filter((_, i) => !indexs.includes(i))
    );
    setValue(
      'scalerColumns',
      getValues('scalerColumns').filter((_, i) => !indexs.includes(i))
    );
    setValue(
      'columnNames',
      getValues('columnNames').filter((_, i) => !indexs.includes(i))
    );

    nodeDataSave();
  };

  const nodeDataReset = () => {
    const keys = Object.keys(defaultValues) as any;
    keys.forEach((key: any) => {
      setValue(key, (defaultValues as any)[key]);
    });
  };

  return {
    useFormReturn,
    columnLabels,
    addColumn,
    deleteColumns,
    nodeDataReset,
    nodeDataSave,
  };
};

export default useCSVDataForm;

function createDefaultValues(data: BlockData): CSVFormValues {
  const parameterInfo = {} as Record<string, { value: any; defaultValue: any }>;

  data.parameters.forEach((parameter) => {
    parameterInfo[parameter.param] = {
      value: parameter.value,
      defaultValue: parameter.defaultValue,
    };
  });
  const seperator = parameterInfo['seperator'].value;
  const seperatorIsCustom = ![',', '\t', ' '].includes(seperator);

  return {
    name: data.name,
    seperator: seperatorIsCustom ? 'custom' : seperator,
    seperatorValue: seperatorIsCustom ? seperator : '',
    columnNameType: 'name',
    columnNames: parameterInfo.column_names.value,
    allColumnType: 'auto',
    columnTypes: parameterInfo.column_types.value,
    idColumn: parameterInfo.id_column.value === null ? 'none' : `${parameterInfo.id_column.value}`,
    allDeleteColumn: false,
    deleteColumns: parameterInfo.delete_columns.value,
    allTargetColumn: false,
    targetColumns: parameterInfo.target_columns.value,
    nan: parameterInfo.nan.value ?? 'none',
    allFillNanValue: '',
    fillNanValues: parameterInfo.fill_nan_value.value,
    encoding: parameterInfo.encoding.value ?? 'none',
    allEncodingColumn: false,
    encodingColumns: parameterInfo.encoding_columns.value,
    scaler: parameterInfo.scaler.value ?? 'none',
    allScalerColumn: false,
    scalerColumns: parameterInfo.scaler_columns.value,
  };
}

function createTableColumnLabels(minTotalColumns: number, totalColumns = 20) {
  const result = [] as string[];
  totalColumns = totalColumns < minTotalColumns ? minTotalColumns + 1 : totalColumns;

  for (let i = 0; i < totalColumns; i++) {
    const char = code2LargeAlphabetString(i);
    result.push(char);
  }

  return result;
}
